import {
  Avatar,
  Box,
  Card,
  Center,
  Chip,
  Group,
  Rating,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import type { FeedbackTag, VisitWithRelations } from '@markku/types'
import { formatDate } from '@markku/utils'
import { IconClock, IconMapPin } from '@tabler/icons-react'
import { useState } from 'react'
import { feedbackHooks, feedbackTagHooks } from '../../lib/trpcHooks/feedback'

interface FeedbackModalProps {
  opened: boolean
  onClose: () => void
  visit: VisitWithRelations
}

export function FeedbackModal({ opened, onClose, visit }: FeedbackModalProps) {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [selectedTags, setSelectedTags] = useState<number[]>([])

  // Fetch available feedback tags
  const { data: tags } = feedbackTagHooks.useList()

  // Create feedback mutation
  const { mutateAsync: createFeedback, isPending: isCreatingFeedback } =
    feedbackHooks.useCreate()

  // Filter tags based on rating
  const getFilteredTags = (): FeedbackTag[] => {
    if (!tags?.data) return []

    if (rating >= 4) {
      // High ratings (4-5): Show positive tags
      return tags.data.filter((tag) => tag.category === 'positive')
    } else if (rating > 0 && rating <= 3) {
      // Low ratings (1-3): Show negative tags
      return tags.data.filter((tag) => tag.category === 'negative')
    }
    return []
  }

  const getTagPrompt = (): string => {
    if (rating >= 4) {
      return 'Mikä meni hyvin?'
    } else if (rating > 0 && rating <= 3) {
      return 'Mitä voisi parantaa?'
    }
    return ''
  }

  const handleSubmitFeedback = async () => {
    try {
      await createFeedback({
        data: {
          visitId: visit.id,
          ratingValue: rating,
          description: comment || null,
          tagIds: selectedTags.map((tagId) => ({ feedbackTagId: tagId })),
        },
      })
      notifications.show({
        title: 'Kiitos palautteesta!',
        message: 'Palautteesi on tallennettu onnistuneesti.',
        color: 'green',
      })
      handleClose()
    } catch (error) {
      console.error(error)
      notifications.show({
        title: 'Virhe',
        message: 'Palautteen tallentaminen epäonnistui. Yritä uudelleen.',
        color: 'red',
      })
    }
  }

  const handleClose = () => {
    // Reset form state
    setRating(0)
    setComment('')
    setSelectedTags([])
    onClose()
  }

  const filteredTags = getFilteredTags()
  const tagPrompt = getTagPrompt()

  // Extract visit information
  const visitDate = new Date(visit.startTime)
  const endTime = new Date(visit.endTime)
  const technician = visit.visitAttendees?.[0]?.user

  // Calculate duration in hours
  const durationMs = endTime.getTime() - visitDate.getTime()
  const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 10) / 10

  // Format date and time
  const dayName = visitDate.toLocaleDateString('fi-FI', { weekday: 'short' })
  const dateStr = formatDate(visitDate)
  const startTimeStr = visitDate.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const endTimeStr = endTime.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <AppModal
      opened={opened}
      onClose={handleClose}
      title="Miten käynti meni?"
      size="md"
      cancelProps={{
        label: 'Peruuta',
        onClick: handleClose,
        disabled: isCreatingFeedback,
      }}
      confirmProps={{
        label: 'Lähetä palaute',
        onClick: handleSubmitFeedback,
        loading: isCreatingFeedback,
        disabled: rating === 0,
      }}
    >
      <Stack gap="xl">
        {/* Visit info reminder */}
        <Card withBorder p="md" bg="gray.0">
          <Stack gap="xs">
            {/* Visit title */}
            <Text size="sm" fw={500}>
              {visit.title || 'Käynti'}
            </Text>

            {/* Date, time and duration */}
            <Group gap="xs">
              <IconClock size={16} color="var(--mantine-color-gray-6)" />
              <Text size="xs" c="dimmed">
                {dayName} {dateStr} • {startTimeStr} - {endTimeStr} (
                {durationHours}h)
              </Text>
            </Group>

            {/* Technician */}
            {technician && (
              <Group gap="xs">
                <Avatar size={20} radius="xl" color="blue">
                  {technician.firstName[0]}
                  {technician.lastName[0]}
                </Avatar>
                <Text size="xs" c="dimmed">
                  {technician.firstName} {technician.lastName}
                </Text>
              </Group>
            )}

            {/* Location */}
            {visit.location && (
              <Group gap="xs">
                <IconMapPin size={16} color="var(--mantine-color-gray-6)" />
                <Text size="xs" c="dimmed">
                  {visit.location}
                </Text>
              </Group>
            )}
          </Stack>
        </Card>

        {/* Step 1: Overall Rating */}
        <Box>
          <Text size="md" fw={600} mb="sm" ta="center">
            Miten arvioisit Markun käyntiä?
          </Text>
          <Center>
            <Rating value={rating} onChange={setRating} size="xl" count={5} />
          </Center>
          {rating > 0 && (
            <Text size="xs" c="dimmed" ta="center" mt="xs">
              {rating >= 4
                ? 'Hienoa, että käynti meni hyvin!'
                : rating === 3
                  ? 'Kiitos palautteestasi'
                  : 'Pahoittelut, että käynti ei täyttänyt odotuksiasi'}
            </Text>
          )}
        </Box>

        {/* Step 2: Conditional Tags (only show after rating is selected) */}
        {rating > 0 && filteredTags.length > 0 && (
          <Box>
            <Text size="md" fw={600} mb="sm" ta="center">
              {tagPrompt}
            </Text>
            <Chip.Group
              multiple
              value={selectedTags.map(String)}
              onChange={(value) => setSelectedTags(value.map(Number))}
            >
              <Center>
                <Group gap="xs">
                  {filteredTags.map((tag) => (
                    <Chip key={tag.id} value={String(tag.id)} size="sm">
                      {tag.name}
                    </Chip>
                  ))}
                </Group>
              </Center>
            </Chip.Group>
          </Box>
        )}

        {/* Step 3: Optional Comment (show after rating is selected) */}
        {rating > 0 && (
          <Box>
            <Text size="md" fw={600} mb="sm" ta="center">
              Vapaa palaute (valinnainen)
            </Text>
            <Textarea
              placeholder={
                rating >= 4
                  ? 'Kerro mitä erityisesti arvostit käynnissä...'
                  : 'Kerro miten voisimme palvella sinua paremmin...'
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              minRows={3}
            />
          </Box>
        )}
      </Stack>
    </AppModal>
  )
}
